/**
 * The side navigation's expanded/collapsed choice, kept in a cookie (#15618).
 *
 * WHY NOT LOCAL STORAGE. sai-ui's `SidebarProvider` persists `open` to
 * `localStorage` by default, which the server cannot read: every hard load
 * then rendered the sidebar expanded and collapsed it after hydration,
 * shifting the whole page ~152px for anyone who had collapsed it. A cookie is
 * read by the root layout, so the first paint is already the viewer's choice.
 *
 * Shared by the server read (`readSidebarDefaultOpen` in the root layout) and
 * the client write (`SideNavigationShell`), so the two cannot disagree about
 * the name or the encoding.
 */

export const SIDEBAR_STATE_COOKIE = 'app-sidebar-state'

/**
 * The admin shell's own sai-ui sidebar (#14982), same encoding. A separate
 * cookie: collapsing the admin list is not a choice about the app's rail.
 * It replaces the `admin-sidebar-collapsed` localStorage key, which the
 * server could not read, so the old sidebar painted an empty `<aside>` until
 * hydration (docs/design-system/ADOPTION_DECISIONS.md § 5, Evidence).
 */
export const ADMIN_SIDEBAR_STATE_COOKIE = 'admin-sidebar-state'

/** A year: the choice is a standing preference, like the theme. */
const SIDEBAR_STATE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

/** Expanded unless the cookie explicitly says collapsed. */
export function parseSidebarStateCookie(value: string | undefined): boolean {
  return value !== 'collapsed'
}

/** The `document.cookie` assignment that records `open`. */
export function serializeSidebarStateCookie(
  open: boolean,
  name: string = SIDEBAR_STATE_COOKIE,
): string {
  const value = open ? 'expanded' : 'collapsed'
  return `${name}=${value}; path=/; max-age=${SIDEBAR_STATE_MAX_AGE_SECONDS}; samesite=lax`
}
